import { useState } from 'react';
import { Link } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/composer.pb';
import { DynamicFieldKey_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { DocumentSelectorModal } from '@frontend/document-selector-modal';
import { useCombinedDocumentsListV3 } from '@frontend/documents-hooks';
import { formatDynamicFieldKey } from '@frontend/string';
import {
  DynamicFieldAttributes,
  useDynamicFieldInsertion,
  useEmptySpaceInsertion,
  useLinkInsertion,
  useModalControl,
} from '@frontend/design-system';
import { useContentComposer } from '../../content-composer/content-composer-provider';
import { SectionBase } from '../../content-composer/section-base/section-base';
import { CustomToolbar } from '../atoms/toolbars/custom-toolbar';
import { OptionsToolbar } from '../atoms/toolbars/options-toolbar';
import type { ToolbarView } from '../atoms/toolbars/types';

type Props = {
  selectedDynamicField?: DynamicFieldAttributes;
  selectRTEToolbar: () => void;
};

export const RTEOptionsToolbar = ({ selectedDynamicField, selectRTEToolbar }: Props) => {
  const { form, locationIds, setForm } = useContentComposer();
  const [showCustom, setShowCustom] = useState(false);

  const insertDynamicField = useDynamicFieldInsertion();
  const insertEmptySpace = useEmptySpaceInsertion();
  const insertLink = useLinkInsertion();

  const handleDynamicFieldInsertion = (dynamicField: DynamicFieldAttributes) => {
    insertDynamicField(dynamicField, true);
    insertEmptySpace();
    setShowCustom(false);
    selectRTEToolbar();
  };

  const handleLinkInsertion = (link: Link) => {
    if (!link.text) return;
    insertLink(link.url, link.text);
    insertEmptySpace();
    setShowCustom(false);
    selectRTEToolbar();
  };

  const handleLinkClick = (link: Link) => {
    switch (link.linkType) {
      case 'form':
        // let the form flow open the document selector modal
        break;
      case 'custom':
        // let the user input a custom text and link
        break;
      case 'schedule':
        handleDynamicFieldInsertion({
          label: 'Schedule Link',
          symbol: formatDynamicFieldKey(DynamicFieldKey_Enum.SCHEDULE_LINK),
          val: DynamicFieldKey_Enum.SCHEDULE_LINK,
        });
        break;
      case 'phone':
        handleDynamicFieldInsertion({
          label: 'Practice Phone',
          symbol: formatDynamicFieldKey(DynamicFieldKey_Enum.BUSINESS_GROUP_PHONE),
          val: DynamicFieldKey_Enum.BUSINESS_GROUP_PHONE,
        });
        break;
      case 'address':
        handleDynamicFieldInsertion({
          label: 'Practice Address',
          symbol: formatDynamicFieldKey(DynamicFieldKey_Enum.BUSINESS_GROUP_ADDRESS),
          val: DynamicFieldKey_Enum.BUSINESS_GROUP_ADDRESS,
        });
        break;
      case 'confirmation':
        handleDynamicFieldInsertion({
          label: 'Confirmation Link',
          symbol: formatDynamicFieldKey(DynamicFieldKey_Enum.CALL_TO_ACTION_LINK),
          val: DynamicFieldKey_Enum.CALL_TO_ACTION_LINK,
        });
        break;
      default:
    }
  };

  // FORM
  const documents = useCombinedDocumentsListV3(locationIds);
  const { modalProps, triggerProps } = useModalControl();
  const openDocumentSelectorModal = () => {
    triggerProps.onClick();
  };
  const updateDocumentId = (documentId: string) => {
    if (selectedDynamicField) handleDynamicFieldInsertion(selectedDynamicField);
    const documentType = documents?.find((form) => form.id === documentId)?.documentType ?? 'form';
    setForm?.({ documentId, documentType }); // set the formId at the consumer level
    modalProps.onClose();
  };

  const setView = (view: ToolbarView) => {
    switch (view) {
      case 'custom':
        setShowCustom(true);
        break;

      case 'options':
        setShowCustom(false);
        break;

      default:
        setShowCustom(false);
        selectRTEToolbar();
        break;
    }
  };

  return (
    <>
      <SectionBase.Toolbar position='bottom-center'>
        {showCustom ? (
          <CustomToolbar
            setLink={handleLinkInsertion}
            setView={setView}
            showLinkText={true}
            css={{
              width: 485,
            }}
          />
        ) : (
          <OptionsToolbar setLink={handleLinkClick} openFormsModal={openDocumentSelectorModal} setView={setView} />
        )}
      </SectionBase.Toolbar>
      <DocumentSelectorModal
        documentId={form?.documentId}
        documents={documents}
        modalProps={modalProps}
        selectedLocationIds={locationIds}
        updateDocumentId={updateDocumentId}
      />
    </>
  );
};
